/**
 * GUS Version Manager Component
 *
 * Manages GUS application versions and staged deployments.
 * Supports version pinning for compliance requirements.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Grid,
  Tabs,
  Tab,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Rocket as DeployIcon,
  Lock as PinIcon,
  LockOpen as UnpinIcon,
  History as HistoryIcon,
  NewReleases as NewIcon,
  CheckCircle as StableIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  ExpandMore as ExpandIcon,
  Close as CloseIcon,
  GitHub as GitHubIcon,
} from '@mui/icons-material';
import api from '../services/api';
import { format } from 'date-fns';

interface Release {
  id: string;
  version_tag: string;
  semver_major: number;
  semver_minor: number;
  semver_patch: number;
  frontend_image: string;
  backend_image: string;
  release_date: string;
  release_notes: string | null;
  is_stable: boolean;
  is_latest: boolean;
  is_lts: boolean;
  is_deprecated: boolean;
  rollout_status: string;
  rollout_percentage: number | null;
  created_at: string;
}

interface ProjectVersion {
  project_id: string;
  project_name: string;
  current_version: string | null;
  version_policy: string;
  version_pinned_to: string | null;
  version_pinned_at: string | null;
  version_pinned_by: string | null;
  version_pin_reason: string | null;
  latest_available: string | null;
  update_available: boolean;
}

interface VersionHistory {
  id: string;
  from_version: string | null;
  to_version: string;
  deployment_type: string;
  status: string;
  initiated_by: string | null;
  initiated_at: string;
  completed_at: string | null;
  notes: string | null;
  error_message: string | null;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => (
  <div hidden={value !== index} style={{ paddingTop: 16 }}>
    {value === index && children}
  </div>
);

// Helper to compare semver versions and determine deployment type
const getDeploymentType = (
  targetVersion: string,
  currentVersion: string | null
): 'upgrade' | 'rollback' | 'current' | 'initial' => {
  if (!currentVersion) return 'initial';
  if (targetVersion === currentVersion) return 'current';

  // Parse versions (e.g., "v3.0.11" -> [3, 0, 11])
  const parseVersion = (v: string) => {
    const match = v.match(/v?(\d+)\.(\d+)\.(\d+)/);
    if (!match) return [0, 0, 0];
    return [parseInt(match[1]), parseInt(match[2]), parseInt(match[3])];
  };

  const [tMajor, tMinor, tPatch] = parseVersion(targetVersion);
  const [cMajor, cMinor, cPatch] = parseVersion(currentVersion);

  if (tMajor > cMajor) return 'upgrade';
  if (tMajor < cMajor) return 'rollback';
  if (tMinor > cMinor) return 'upgrade';
  if (tMinor < cMinor) return 'rollback';
  if (tPatch > cPatch) return 'upgrade';
  if (tPatch < cPatch) return 'rollback';

  return 'current';
};

const VersionManager: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [releases, setReleases] = useState<Release[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Deploy dialog state
  const [deployDialogOpen, setDeployDialogOpen] = useState(false);
  const [selectedRelease, setSelectedRelease] = useState<Release | null>(null);
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');
  const [deployNotes, setDeployNotes] = useState('');
  const [deploying, setDeploying] = useState(false);

  // Projects with their version info
  const [projectVersions, setProjectVersions] = useState<ProjectVersion[]>([]);

  // Version history for selected project
  const [versionHistory, setVersionHistory] = useState<VersionHistory[]>([]);
  const [selectedHistoryProject, setSelectedHistoryProject] = useState<string>('');

  // Notes dialog state
  const [notesDialogOpen, setNotesDialogOpen] = useState(false);
  const [notesDialogTitle, setNotesDialogTitle] = useState('');
  const [notesDialogContent, setNotesDialogContent] = useState('');
  const [loadingNotes, setLoadingNotes] = useState(false);

  const loadReleases = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/releases');
      setReleases(response.data);
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load releases');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadProjectVersions = useCallback(async () => {
    try {
      // Use batch endpoint to get all project versions in one call
      const response = await api.get('/releases/projects/versions/all');
      setProjectVersions(response.data);
    } catch (err: any) {
      console.error('Failed to load project versions:', err);
    }
  }, []);

  const loadVersionHistory = useCallback(async (projectId: string) => {
    try {
      const response = await api.get(`/releases/projects/${projectId}/version-history`);
      setVersionHistory(response.data);
    } catch (err: any) {
      console.error('Failed to load version history:', err);
      setVersionHistory([]);
    }
  }, []);

  // Fetch release notes from GitHub
  const fetchGitHubNotes = useCallback(async (versionTag: string) => {
    setLoadingNotes(true);
    setNotesDialogTitle(`Release Notes - ${versionTag}`);
    setNotesDialogOpen(true);
    setNotesDialogContent('');

    try {
      const response = await api.get(`/releases/${versionTag}/github-notes`);
      setNotesDialogContent(response.data.release_notes || 'No release notes available.');
    } catch (err: any) {
      // Fallback to stored notes if GitHub fetch fails
      const release = releases.find(r => r.version_tag === versionTag);
      if (release?.release_notes) {
        setNotesDialogContent(release.release_notes);
      } else {
        setNotesDialogContent('No release notes available for this version.');
      }
    } finally {
      setLoadingNotes(false);
    }
  }, [releases]);

  // Show notes in dialog (for history or local notes)
  const showNotes = useCallback((title: string, notes: string | null) => {
    setNotesDialogTitle(title);
    setNotesDialogContent(notes || 'No notes available.');
    setNotesDialogOpen(true);
    setLoadingNotes(false);
  }, []);

  useEffect(() => {
    loadReleases();
    loadProjectVersions();
  }, [loadReleases, loadProjectVersions]);

  useEffect(() => {
    if (selectedHistoryProject) {
      loadVersionHistory(selectedHistoryProject);
    }
  }, [selectedHistoryProject, loadVersionHistory]);

  const handleDeployVersion = async () => {
    if (!selectedRelease || !selectedProjectId) return;

    setDeploying(true);
    try {
      await api.post(`/releases/projects/${selectedProjectId}/deploy-version`, {
        target_version: selectedRelease.version_tag,
        notes: deployNotes,
      });
      setSuccess(`Version ${selectedRelease.version_tag} deployment initiated`);
      setDeployDialogOpen(false);
      setDeployNotes('');
      loadProjectVersions();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to deploy version');
    } finally {
      setDeploying(false);
    }
  };

  const handlePinVersion = async (projectId: string, version: string, reason: string) => {
    try {
      await api.patch(`/releases/projects/${projectId}/version`, {
        version_policy: 'pinned',
        version_pinned_to: version,
        version_pin_reason: reason,
      });
      setSuccess(`Version pinned to ${version}`);
      loadProjectVersions();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to pin version');
    }
  };

  const handleUnpinVersion = async (projectId: string) => {
    try {
      await api.patch(`/releases/projects/${projectId}/version`, {
        version_policy: 'auto',
      });
      setSuccess('Version unpinned - project will receive automatic updates');
      loadProjectVersions();
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to unpin version');
    }
  };

  const getRolloutStatusChip = (status: string, percentage: number | null) => {
    switch (status) {
      case 'staging':
        return <Chip size="small" label="Staging" color="info" icon={<ScheduleIcon />} />;
      case 'canary':
        return <Chip size="small" label={`Canary ${percentage}%`} color="warning" />;
      case 'gradual':
        return <Chip size="small" label={`Gradual ${percentage}%`} color="warning" />;
      case 'general':
        return <Chip size="small" label="General" color="success" icon={<StableIcon />} />;
      case 'deprecated':
        return <Chip size="small" label="Deprecated" color="error" icon={<WarningIcon />} />;
      default:
        return <Chip size="small" label={status} />;
    }
  };

  if (loading && releases.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Action Bar */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="body2" color="text.secondary">
          Manage GUS application versions and staged deployments
        </Typography>
        <Tooltip title="Refresh">
          <IconButton onClick={() => { loadReleases(); loadProjectVersions(); }} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="Available Releases" />
        <Tab label="Project Versions" />
        <Tab label="Version History" />
      </Tabs>

      {/* Tab 1: Available Releases */}
      <TabPanel value={tabValue} index={0}>
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Version</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Rollout</TableCell>
                <TableCell>Release Date</TableCell>
                <TableCell>Notes</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {releases.map((release) => (
                <TableRow
                  key={release.id}
                  sx={{
                    bgcolor: release.is_latest ? 'success.50' : release.is_deprecated ? 'error.50' : 'inherit',
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {release.version_tag}
                      </Typography>
                      {release.is_latest && <Chip size="small" label="Latest" color="success" />}
                      {release.is_lts && <Chip size="small" label="LTS" color="primary" />}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {release.is_stable ? (
                      <Chip size="small" label="Stable" color="success" icon={<StableIcon />} />
                    ) : (
                      <Chip size="small" label="Pre-release" color="warning" icon={<NewIcon />} />
                    )}
                  </TableCell>
                  <TableCell>
                    {getRolloutStatusChip(release.rollout_status, release.rollout_percentage)}
                  </TableCell>
                  <TableCell>
                    {format(new Date(release.release_date), 'MMM dd, yyyy')}
                  </TableCell>
                  <TableCell>
                    {release.release_notes ? (
                      <Tooltip title="Click to view full release notes">
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'pointer',
                            maxWidth: 200,
                            '&:hover': {
                              color: 'primary.main',
                            },
                          }}
                          onClick={() => fetchGitHubNotes(release.version_tag)}
                        >
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            noWrap
                            sx={{ flex: 1 }}
                          >
                            {release.release_notes}
                          </Typography>
                          <ExpandIcon fontSize="small" color="action" />
                        </Box>
                      </Tooltip>
                    ) : (
                      <Tooltip title="Fetch notes from GitHub">
                        <IconButton
                          size="small"
                          onClick={() => fetchGitHubNotes(release.version_tag)}
                        >
                          <GitHubIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                  <TableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<DeployIcon />}
                      onClick={() => {
                        setSelectedRelease(release);
                        setDeployDialogOpen(true);
                      }}
                      disabled={release.is_deprecated}
                    >
                      Deploy
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Tab 2: Project Versions */}
      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={2}>
          {projectVersions.map((pv) => (
            <Grid item xs={12} md={6} lg={4} key={pv.project_id}>
              <Card
                sx={{
                  borderLeft: 4,
                  borderColor: pv.update_available
                    ? 'warning.main'
                    : pv.version_policy === 'pinned'
                    ? 'info.main'
                    : 'success.main',
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6">{pv.project_name}</Typography>
                    {pv.version_policy === 'pinned' ? (
                      <Tooltip title={`Pinned: ${pv.version_pin_reason || 'No reason specified'}`}>
                        <Chip size="small" icon={<PinIcon />} label="Pinned" color="info" />
                      </Tooltip>
                    ) : (
                      <Chip size="small" label="Auto-update" color="success" />
                    )}
                  </Box>

                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Current Version
                  </Typography>
                  <Typography variant="h5" fontWeight={600} gutterBottom>
                    {pv.current_version || 'Not deployed'}
                  </Typography>

                  {pv.update_available && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      Update available: {pv.latest_available}
                    </Alert>
                  )}

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    {pv.version_policy === 'pinned' ? (
                      <Button
                        size="small"
                        startIcon={<UnpinIcon />}
                        onClick={() => handleUnpinVersion(pv.project_id)}
                      >
                        Unpin
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        startIcon={<PinIcon />}
                        onClick={() => {
                          const reason = prompt('Enter reason for pinning this version:');
                          if (reason && pv.current_version) {
                            handlePinVersion(pv.project_id, pv.current_version, reason);
                          }
                        }}
                        disabled={!pv.current_version}
                      >
                        Pin Version
                      </Button>
                    )}
                    <Button
                      size="small"
                      startIcon={<HistoryIcon />}
                      onClick={() => {
                        setSelectedHistoryProject(pv.project_id);
                        setTabValue(2);
                      }}
                    >
                      History
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      {/* Tab 3: Version History */}
      <TabPanel value={tabValue} index={2}>
        <FormControl sx={{ mb: 2, minWidth: 300 }}>
          <InputLabel>Select Project</InputLabel>
          <Select
            value={selectedHistoryProject}
            onChange={(e) => setSelectedHistoryProject(e.target.value)}
            label="Select Project"
          >
            {projectVersions.map((pv) => (
              <MenuItem key={pv.project_id} value={pv.project_id}>
                {pv.project_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {selectedHistoryProject && (
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Type</TableCell>
                  <TableCell>From</TableCell>
                  <TableCell>To</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Initiated By</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {versionHistory.map((h) => (
                  <TableRow key={h.id}>
                    <TableCell>
                      <Chip
                        size="small"
                        label={h.deployment_type}
                        color={
                          h.deployment_type === 'upgrade'
                            ? 'success'
                            : h.deployment_type === 'rollback'
                            ? 'warning'
                            : 'info'
                        }
                      />
                    </TableCell>
                    <TableCell>{h.from_version || '-'}</TableCell>
                    <TableCell>
                      <Typography fontWeight={600}>{h.to_version}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={h.status}
                        color={
                          h.status === 'completed'
                            ? 'success'
                            : h.status === 'recorded'
                            ? 'info'
                            : h.status === 'failed'
                            ? 'error'
                            : h.status === 'in_progress'
                            ? 'warning'
                            : 'default'
                        }
                      />
                    </TableCell>
                    <TableCell>{h.initiated_by || '-'}</TableCell>
                    <TableCell>{format(new Date(h.initiated_at), 'MMM dd, yyyy HH:mm')}</TableCell>
                    <TableCell>
                      {(h.notes || h.error_message) ? (
                        <Tooltip title="Click to view full notes">
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              cursor: 'pointer',
                              maxWidth: 150,
                              '&:hover': {
                                color: 'primary.main',
                              },
                            }}
                            onClick={() => showNotes(
                              `${h.deployment_type.charAt(0).toUpperCase() + h.deployment_type.slice(1)} to ${h.to_version}`,
                              h.notes || h.error_message
                            )}
                          >
                            <Typography
                              variant="body2"
                              color={h.error_message ? 'error' : 'text.secondary'}
                              noWrap
                              sx={{ flex: 1 }}
                            >
                              {h.notes || h.error_message}
                            </Typography>
                            <ExpandIcon fontSize="small" color="action" />
                          </Box>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {versionHistory.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="text.secondary">No version history</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Deploy Version Dialog */}
      <Dialog open={deployDialogOpen} onClose={() => setDeployDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Deploy Version {selectedRelease?.version_tag}</DialogTitle>
        <DialogContent>
          {selectedRelease && (
            <Box sx={{ mt: 2 }}>
              {(() => {
                const selectedProject = projectVersions.find(pv => pv.project_id === selectedProjectId);
                const deployType = selectedProject
                  ? getDeploymentType(selectedRelease.version_tag, selectedProject.current_version)
                  : null;

                return (
                  <>
                    {deployType === 'rollback' && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        <strong>Rollback:</strong> This will roll back to an older version ({selectedRelease.version_tag}).
                        Current version: {selectedProject?.current_version}
                      </Alert>
                    )}
                    {deployType === 'upgrade' && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <strong>Upgrade:</strong> This will upgrade from {selectedProject?.current_version} to {selectedRelease.version_tag}.
                      </Alert>
                    )}
                    {deployType === 'current' && (
                      <Alert severity="success" sx={{ mb: 2 }}>
                        <strong>Current Version:</strong> {selectedRelease.version_tag} is already deployed to this project.
                      </Alert>
                    )}
                    {deployType === 'initial' && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <strong>Initial Deployment:</strong> This project has no version deployed yet.
                      </Alert>
                    )}
                    {!deployType && (
                      <Alert severity="info" sx={{ mb: 2 }}>
                        Select a project to see deployment details.
                      </Alert>
                    )}
                  </>
                );
              })()}

              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel>Target Project</InputLabel>
                <Select
                  value={selectedProjectId}
                  onChange={(e) => setSelectedProjectId(e.target.value)}
                  label="Target Project"
                >
                  {projectVersions.map((pv) => {
                    const deployType = getDeploymentType(selectedRelease.version_tag, pv.current_version);
                    return (
                      <MenuItem key={pv.project_id} value={pv.project_id}>
                        {pv.project_name} (current: {pv.current_version || 'none'})
                        {deployType === 'current' && ' ✓'}
                        {deployType === 'rollback' && ' ↩'}
                        {deployType === 'upgrade' && ' ↑'}
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                multiline
                rows={3}
                label="Deployment Notes (optional)"
                value={deployNotes}
                onChange={(e) => setDeployNotes(e.target.value)}
                placeholder="Reason for deployment, change notes, etc."
              />

              {selectedRelease.release_notes && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Release Notes
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {selectedRelease.release_notes}
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeployDialogOpen(false)}>Cancel</Button>
          {(() => {
            const selectedProject = projectVersions.find(pv => pv.project_id === selectedProjectId);
            const deployType = selectedProject && selectedRelease
              ? getDeploymentType(selectedRelease.version_tag, selectedProject.current_version)
              : null;
            const isCurrent = deployType === 'current';
            const buttonLabel = deploying ? 'Deploying...'
              : isCurrent ? 'Already Deployed'
              : deployType === 'rollback' ? 'Rollback'
              : deployType === 'upgrade' ? 'Upgrade'
              : 'Deploy';

            return (
              <Button
                variant="contained"
                color={deployType === 'rollback' ? 'warning' : 'primary'}
                onClick={handleDeployVersion}
                disabled={!selectedProjectId || deploying || isCurrent}
                startIcon={deploying ? <CircularProgress size={16} /> : <DeployIcon />}
              >
                {buttonLabel}
              </Button>
            );
          })()}
        </DialogActions>
      </Dialog>

      {/* Release Notes Dialog */}
      <Dialog
        open={notesDialogOpen}
        onClose={() => setNotesDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <GitHubIcon color="primary" />
            {notesDialogTitle}
          </Box>
          <IconButton onClick={() => setNotesDialogOpen(false)} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {loadingNotes ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              sx={{
                whiteSpace: 'pre-wrap',
                fontFamily: 'monospace',
                fontSize: '0.875rem',
                lineHeight: 1.6,
                p: 1,
                bgcolor: 'grey.50',
                borderRadius: 1,
                maxHeight: '60vh',
                overflow: 'auto',
              }}
            >
              {notesDialogContent}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotesDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default VersionManager;
